import React, { useState, useEffect } from 'react';
import Navbar from '../Navbar/Navbar'; // Assuming this is your existing Navbar
import Footer from '../Footer/Footer'; // Assuming this is your existing Footer
import './Carrer.css'; // Ensure you have this corresponding CSS file in the same folder

// --- SVG Icons ---
// These are included directly so the file is self-contained.
const BriefcaseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>;
const UserIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>;
const UploadCloudIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path><polyline points="16 16 12 12 8 16"></polyline></svg>;
const FileIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>;
const RefreshIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>;

// --- API Configuration ---
const API_URL = 'https://api.hitkarihospital.com:5005/api';

// --- Main Component ---
const CareerPageV3 = () => {
    // --- State Management ---
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        jobRole: '', firstName: '', lastName: '', gender: '', maritalStatus: '',
        dob: '', contactNo: '', email: '', address: '',
        workingStatus: 'fresher', captchaInput: ''
    });
    const [resumeFile, setResumeFile] = useState(null);
    const [errors, setErrors] = useState({});
    const [captchaText, setCaptchaText] = useState('');
    const [jobRoles, setJobRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // --- Effects ---
    // Fetch captcha and job roles on component mount
    useEffect(() => {
        regenerateCaptcha();
        const fetchJobs = async () => {
            try {
                const response = await fetch(`${API_URL}/jobs`);
                if (!response.ok) throw new Error('Network response was not ok');
                const data = await response.json();
                setJobRoles(data);
            } catch (error) {
                console.error("Failed to fetch job roles:", error);
                setErrors(prev => ({ ...prev, jobRole: "Could not load job roles. Is the backend running?" }));
            }
        };
        fetchJobs();
    }, []);

    // --- Handlers ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setResumeFile(file);
            if (errors.resume) setErrors(prev => ({ ...prev, resume: null }));
        }
    };
    
    const removeFile = () => {
        setResumeFile(null);
        document.getElementById('resume').value = null; // Clear the file input
    };

    const regenerateCaptcha = () => {
         setCaptchaText(Math.random().toString(36).substring(2, 8).toUpperCase());
    };

    // --- Form Submission Logic ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Final validation before submitting
        const finalErrors = {};
        if (!resumeFile) finalErrors.resume = "Please upload your resume.";
        if (formData.captchaInput.toLowerCase() !== captchaText.toLowerCase()) {
            finalErrors.captchaInput = "Captcha does not match.";
        }
        setErrors(prev => ({...prev, ...finalErrors}));

        if (Object.keys(finalErrors).length > 0) {
            if(finalErrors.captchaInput) regenerateCaptcha();
            return;
        }

        setIsLoading(true);

        const applicationData = new FormData();
        for (const key in formData) {
            applicationData.append(key, formData[key]);
        }
        applicationData.append('resume', resumeFile);

        try {
            const response = await fetch(`${API_URL}/apply`, {
                method: 'POST',
                body: applicationData,
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.msg || 'An unknown error occurred.');
            }
            setCurrentStep('submitted');
        } catch (error) {
            console.error('Submission failed:', error);
            setErrors(prev => ({...prev, form: error.message}));
            regenerateCaptcha();
        } finally {
            setIsLoading(false);
        }
    };

    // --- Step Navigation & Validation ---
    const nextStep = () => {
        let newErrors = {};
        if (currentStep === 1) {
            if (!formData.jobRole) newErrors.jobRole = "Please select a job role.";
            if (!formData.firstName) newErrors.firstName = "First name is required.";
            if (!formData.lastName) newErrors.lastName = "Last name is required.";
            if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "A valid email is required.";
        } else if (currentStep === 2) {
            if (!formData.contactNo) newErrors.contactNo = "Contact number is required.";
            if (!formData.dob) newErrors.dob = "Date of birth is required.";
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0) {
            setCurrentStep(s => s + 1);
        }
    };
    
    const prevStep = () => setCurrentStep(s => s - 1);
    
    // --- Conditional Render Logic ---
    if (isLoading) {
        return (
            <div className="career-page-v3">
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>Submitting your application...</p>
                </div>
            </div>
        )
    }

    if (currentStep === 'submitted') {
        return (
            <div className="career-page-v3">
                 <div className="visual-panel">
                    <div className="visual-content">
                        <h2>Thank You!</h2>
                        <p>Your application has been received. We appreciate your interest and will be in touch shortly.</p>
                    </div>
                    <div className="shape shape1"></div><div className="shape shape2"></div>
                </div>
                 <div className="form-panel success-panel">
                    <svg className="success-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-1.25 17.292l-4.5-4.364 1.857-1.858 2.643 2.506 5.643-5.784 1.857 1.857-7.5 7.643z"/></svg>
                    <h2>Application Submitted</h2>
                    <p>We're reviewing your profile and will contact you if you're a good fit.</p>
                </div>
            </div>
        )
    }

    const progressPercentage = ((currentStep - 1) / 2) * 100;

    return (
        <>
         <Navbar />
        <div className="career-page-v3">
            <div className="visual-panel">
                <div className="visual-content">
                    <h2>Shape the Future</h2>
                    <p>Your next career move could be the one that defines everything. Join a team of innovators and creators.</p>
                </div>
                <div className="shape shape1"></div>
                <div className="shape shape2"></div>
                <div className="shape shape3"></div>
            </div>

            <div className="form-panel">
                <div className="form-header">
                    <h1>Apply Now</h1>
                    <div className="progress-bar">
                        <div className="progress" style={{ width: `${progressPercentage}%` }}></div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    <div className="form-step-container">
                        {currentStep === 1 && (
                            <div className="form-step active-step">
                                <h3 className="step-title">Basic Information</h3>
                                <div className="input-group">
                                    <select id="jobRole" name="jobRole" value={formData.jobRole} onChange={handleChange} required>
                                        <option value="" disabled>Select a Job Role *</option>
                                        {jobRoles.length > 0 ? (
                                            jobRoles.map((job) => (
                                                <option key={job._id} value={job.value}>{job.name}</option>
                                            ))
                                        ) : (
                                            <option disabled>Loading roles...</option>
                                        )}
                                    </select>
                                    {errors.jobRole && <span className="error-message">{errors.jobRole}</span>}
                                </div>
                                <div className="input-grid">
                                    <div className="input-group"><input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} placeholder=" " required/><label htmlFor="firstName">First Name *</label>{errors.firstName && <span className="error-message">{errors.firstName}</span>}</div>
                                    <div className="input-group"><input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} placeholder=" " required/><label htmlFor="lastName">Last Name *</label>{errors.lastName && <span className="error-message">{errors.lastName}</span>}</div>
                                </div>
                                <div className="input-group"><input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder=" " required/><label htmlFor="email">Email Address *</label>{errors.email && <span className="error-message">{errors.email}</span>}</div>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="form-step active-step">
                                <h3 className="step-title">Personal Details</h3>
                                <div className="input-grid">
                                    <div className="input-group"><input type="tel" id="contactNo" name="contactNo" value={formData.contactNo} onChange={handleChange} placeholder=" " required/><label htmlFor="contactNo">Contact Number *</label>{errors.contactNo && <span className="error-message">{errors.contactNo}</span>}</div>
                                    <div className="input-group"><input type="date" id="dob" name="dob" value={formData.dob} onChange={handleChange} placeholder=" " required/><label htmlFor="dob">Date of Birth *</label>{errors.dob && <span className="error-message">{errors.dob}</span>}</div>
                                </div>
                                <div className="input-grid">
                                    <div className="input-group"><select id="gender" name="gender" value={formData.gender} onChange={handleChange}><option value="">Select Gender</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option><option value="prefer_not_to_say">Prefer not to say</option></select></div>
                                    <div className="input-group"><select id="maritalStatus" name="maritalStatus" value={formData.maritalStatus} onChange={handleChange}><option value="">Select Marital Status</option><option value="single">Single</option><option value="married">Married</option><option value="divorced">Divorced</option></select></div>
                                </div>
                                <div className="input-group"><textarea id="address" name="address" rows="3" value={formData.address} onChange={handleChange} placeholder=" "></textarea><label htmlFor="address">Address</label></div>
                            </div>
                        )}
                        
                        {currentStep === 3 && (
                            <div className="form-step active-step">
                                <h3 className="step-title">Final Steps</h3>
                                <div className="final-step-grid">
                                    <div className="input-card">
                                        <label className="card-label">Working Status *</label>
                                        <div className="option-cards-container">
                                            <div className={`option-card ${formData.workingStatus === 'fresher' ? 'selected' : ''}`} onClick={() => setFormData({...formData, workingStatus: 'fresher'})}>
                                                <UserIcon />
                                                <span>Fresher</span>
                                            </div>
                                            <div className={`option-card ${formData.workingStatus === 'experienced' ? 'selected' : ''}`} onClick={() => setFormData({...formData, workingStatus: 'experienced'})}>
                                                <BriefcaseIcon />
                                                <span>Experienced</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="input-card">
                                        <label className="card-label">Upload Resume *</label>
                                        { !resumeFile ? (
                                            <label htmlFor="resume" className="file-drop-zone">
                                                <UploadCloudIcon />
                                                <p><b>Click to upload</b> or drag and drop</p>
                                                <small>PDF, DOC, DOCX (max. 5MB)</small>
                                                <input type="file" id="resume" name="resume" accept=".pdf,.doc,.docx" onChange={handleFileChange} />
                                            </label>
                                        ) : (
                                            <div className="file-info-card">
                                                <FileIcon />
                                                <span className="file-name">{resumeFile.name}</span>
                                                <button type="button" onClick={removeFile} className="remove-file-btn">×</button>
                                            </div>
                                        )}
                                        {errors.resume && <span className="error-message">{errors.resume}</span>}
                                    </div>

                                    <div className="input-card captcha-card">
                                        <label className="card-label">Security Check *</label>
                                        <div className="captcha-wrapper">
                                            <span className="captcha-code">{captchaText}</span>
                                            <button type="button" className="refresh-btn" onClick={regenerateCaptcha}><RefreshIcon /></button>
                                        </div>
                                        <div className="input-group">
                                            <input type="text" id="captchaInput" name="captchaInput" value={formData.captchaInput} onChange={handleChange} placeholder=" " required />
                                            <label htmlFor="captchaInput">Enter the code above</label>
                                        </div>
                                         {errors.captchaInput && <span className="error-message">{errors.captchaInput}</span>}
                                    </div>
                                </div>
                                {errors.form && <div className="error-message form-error">{errors.form}</div>}
                            </div>
                        )}
                    </div>
                    <div className="form-navigation">
                        {currentStep > 1 && (<button type="button" className="nav-btn prev-btn" onClick={prevStep}>Previous</button>)}
                        {currentStep < 3 && (<button type="button" className="nav-btn next-btn" onClick={nextStep}>Next</button>)}
                        {currentStep === 3 && (<button type="submit" className="nav-btn submit-btn" disabled={isLoading}>Apply Now</button>)}
                    </div>
                </form>
            </div>
        </div>
        <Footer/>
        </>
    );
};

export default CareerPageV3;